import {inject, Injectable, signal} from '@angular/core';
import {map, tap} from 'rxjs/operators';
import {finalize, Observable, of} from 'rxjs';
import {InvoiceDocumentApiService} from "../../api/services";
import {InvoiceDocument} from "../../api/models/invoice-document";
import {InvoiceDocumentSearchResult} from "../../api/models/invoice-document-search-result";
import {InvoiceDocumentSearch} from "../../api/models/invoice-document-search";
import {BillingDocument} from "../../api/models/billing-document";
import {InvoiceDocumentLine} from "../../api/models/invoice-document-line";

@Injectable({ providedIn: 'root' })
export class InvoiceDocumentStore {
  private invoiceDocumentApi = inject(InvoiceDocumentApiService);

  // ======== Signals ========
  private _invoices = signal<InvoiceDocument[]>([]);
  readonly invoices = this._invoices.asReadonly();

  private _loading = signal(false);
  readonly loading = this._loading.asReadonly();

  private _totalElements = signal(0);
  readonly totalElements = this._totalElements.asReadonly();

  private _pageIndex = signal(0);
  readonly pageIndex = this._pageIndex.asReadonly();

  private _pageSize = signal(10);
  readonly pageSize = this._pageSize.asReadonly();

  private _currentInvoiceDocument = signal<InvoiceDocument | null>(null);
  readonly currentInvoiceDocument = this._currentInvoiceDocument.asReadonly();

  private _accountCodeFilter = signal<string | null>(null);
  readonly accountCodeFilter = this._accountCodeFilter.asReadonly();

  searchInvoices(
    criteria: Partial<InvoiceDocumentSearch> = {},
    page: number = 0,
    size: number = 10,
    sort: string = 'issueDate',
    order: 'asc' | 'desc' = 'desc'
  ): Observable<InvoiceDocumentSearchResult> {

    this._loading.set(true);
    this._pageIndex.set(page);
    this._pageSize.set(size);

    const body: InvoiceDocumentSearch = {
      issueStartDate: criteria.issueStartDate ?? undefined,
      issueEndDate: criteria.issueEndDate ?? undefined,
      dueStartDate: criteria.dueStartDate ?? undefined,
      dueEndDate: criteria.dueEndDate ?? undefined,
      buyerReference: criteria.buyerReference ?? undefined,
      purchaseOrderReference: criteria.purchaseOrderReference ?? undefined,
      supplierName: criteria.supplierName ?? undefined,
      type: criteria.type ?? undefined,
      services: criteria.services ?? undefined,
      serviceValidationStatus: criteria.serviceValidationStatus ?? undefined,
      completed: criteria.completed ?? undefined,
      dismissed: criteria.dismissed ?? undefined
    };

    return this.invoiceDocumentApi.searchInvoiceDocuments({
      page,
      size,
      sort,
      order,
      body
    }).pipe(
      tap(result => {
        this._invoices.set(result.invoiceDocuments ?? []);
        this._totalElements.set(result.totalElements ?? 0);
      }),
      finalize(() => this._loading.set(false))
    );
  }

  invoiceDocumentByUuid(uuid: string): InvoiceDocument | undefined {
    return this._invoices().find(inv => inv.uuid === uuid);
  }

  upsertInvoiceDocumentInStore(invoice: InvoiceDocument) {
    this._loading.set(true);
    this._invoices.update(invoices => {
      const index = invoices.findIndex(inv => inv.uuid === invoice.uuid);

      if (index !== -1) {
        const updated = [...invoices];
        updated[index] = invoice;
        return updated;
      } else {
        return [...invoices, invoice];
      }
    });
    this._currentInvoiceDocument.set(invoice);
    this._loading.set(false);
  }

  resyncInvoiceDocumentData(invoiceDocumentUuid: string) {
    this._loading.set(true);
    this.invoiceDocumentApi.syncInvoiceDocument({uuid: invoiceDocumentUuid}).subscribe(
      invoiceDocument => {
        this.resetAccountCodeFilter();
        this.upsertInvoiceDocumentInStore(invoiceDocument);
        this._currentInvoiceDocument.set(invoiceDocument);
        this._loading.set(false);
      },
      error => {
        this._loading.set(false);
      }
    )
  }

  loadInvoiceDocument(uuid: string): Observable<InvoiceDocument> {
    this._loading.set(true);
    this.resetAccountCodeFilter();

    return this.invoiceDocumentApi.getInvoiceDocument({ uuid }).pipe(
      tap(invoice => {
        this.upsertInvoiceDocumentInStore(invoice);
        this._currentInvoiceDocument.set(invoice);
        this._loading.set(false);
      }),
      finalize(() => this._loading.set(false))
    );
  }

  loadInvoiceDocumentAndReturn(uuid: string): Observable<InvoiceDocument> {
    const existing = this.invoiceDocumentByUuid(uuid);
    if (existing) {
      this._currentInvoiceDocument.set(existing);
      return of(existing);
    }

    return this.loadInvoiceDocument(uuid);
  }

  linkInvoiceToPO(invoiceDocumentUuid: string, poUuid: string): Observable<InvoiceDocument> {
    this._loading.set(true);
    return this.invoiceDocumentApi.linkInvoiceDocument({
      uuid: invoiceDocumentUuid,
      body: { purchaseOrderUuid: poUuid }
    }).pipe(
      map(billingDocument => {
        const updated = this.attachBillingDocumentToInvoice(billingDocument, poUuid);
        if (!updated) {
          throw new Error('Updated invoice not found in store');
        }
        return updated;
      }),
      finalize(() => this._loading.set(false))
    );
  }

  unlinkInvoiceToPO(invoiceDocumentUuid: string): Observable<InvoiceDocument> {
    this._loading.set(true);

    return this.invoiceDocumentApi.unlinkInvoiceDocument({
      uuid: invoiceDocumentUuid
    }).pipe(
      tap(updatedInvoice => {
        this.upsertInvoiceDocumentInStore(updatedInvoice);
      }),
      finalize(() => this._loading.set(false))
    );
  }

  attachBillingDocumentToInvoice(
    billingDocument: BillingDocument,
    poUuid?: string
  ): InvoiceDocument | undefined {

    if (!billingDocument.invoiceDocumentUuid) {
      return;
    }

    this._loading.set(true);
    let updatedInvoice: InvoiceDocument | undefined;

    this._invoices.update(invoices =>
      invoices.map(inv => {
        if (inv.uuid === billingDocument.invoiceDocumentUuid) {
          updatedInvoice = {
            ...inv,
            billingDocument: billingDocument,
            purchaseOrderUuid: poUuid ?? inv.purchaseOrderUuid
          };
          return updatedInvoice;
        }
        return inv;
      })
    );

    if (this._currentInvoiceDocument()?.uuid === billingDocument.invoiceDocumentUuid && updatedInvoice) {
      this._currentInvoiceDocument.set(updatedInvoice);
    }
    this._loading.set(false);

    return updatedInvoice;
  }

  dismissInvoiceDocument(
    invoiceDocumentUuid: string,
    comment?: string,
    supplierEmails?: Array<string>,
    supplierAction?: 'NO_ACTION' | 'CORRECTED_INVOICE'
  ): Observable<InvoiceDocument> {

    this._loading.set(true);

    return this.invoiceDocumentApi.dismissInvoiceDocument({
      uuid: invoiceDocumentUuid,
      body: { comment, supplierEmails, supplierAction }
    }).pipe(
      tap((updatedInvoice) => {
        this.upsertInvoiceDocumentInStore(updatedInvoice);
      }),
      finalize(() => this._loading.set(false))
    );
  }

  setAccountCodeFilter(accountCode: string | null | undefined) {
    if (!accountCode) {  // reset if no account code selected
      this.resetAccountCodeFilter();
      return;
    }

    this._accountCodeFilter.set(accountCode.charAt(0));
  }

  resetAccountCodeFilter() {
    this._accountCodeFilter.set(null);
  }

  addLineFromCurrentInvoiceDocument(): void {
    let updatedInvoice: InvoiceDocument | null = structuredClone(this._currentInvoiceDocument());
    if (!updatedInvoice) {
      console.warn(`Trying to delete lines of current InvoiceDocument, but current value is : ${updatedInvoice}`);
      return;
    }
    if (!updatedInvoice.invoiceDocumentLines) {
      console.warn(`Trying to delete lines of current InvoiceDocument, but lines value is : ${updatedInvoice.invoiceDocumentLines}`);
      return;
    }
    if (!updatedInvoice.invoiceDocumentLines) {
      updatedInvoice.invoiceDocumentLines = [];
    }
    updatedInvoice.invoiceDocumentLines.push(this.createInvoiceDocumentLine(updatedInvoice));
    this.upsertInvoiceDocumentInStore(updatedInvoice);
  }

  removeLineFromCurrentInvoiceDocument(index: number): void {
    let updatedInvoice: InvoiceDocument | null = structuredClone(this._currentInvoiceDocument());
    if (!updatedInvoice) {
      console.warn(`Trying to delete lines of current InvoiceDocument, but current value is : ${updatedInvoice}`);
      return;
    }
    if (!updatedInvoice.invoiceDocumentLines) {
      console.warn(`Trying to delete lines of current InvoiceDocument, but lines value is : ${updatedInvoice.invoiceDocumentLines}`);
      return;
    }
    updatedInvoice.invoiceDocumentLines?.splice(index, 1);
    this.upsertInvoiceDocumentInStore(updatedInvoice);
  }

  createInvoiceDocumentLine(invoice: InvoiceDocument | null): InvoiceDocumentLine {
    /* NOTE(thomas.menini):
     * for some reason, current invoice document of type `InvoiceDocument` which requires uuid for lines
     * but current invoice document is used for creating a billing document (of peppol invoices)
     * which do not have uuid for lines (all of it is under creation).
     * the actual type is `CompleteInvoiceDocumentWithoutPORequestDto` and sent to api for creation without lines uuid
     * but is not generated by contract at the moment.
     */
    return {
      uuid: null as unknown as string,
      articles: [{
        uuid: null as unknown as string,
        designation: '',
        quantity: 1,
        amountExclVat: 0,
        taxRate: 0,
      }]
    };
  }
}
